import React, { useCallback, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { ListingResource, PropertyField } from '../../system/field';
import {
  ApplicationEvent,
  useAnalytics,
} from '../../contexts/AnalyticsContext';
import { navigateTo } from '../../utils';
import { KEYSTONE_REDIRECT_URL, NGW_REDIRECT_URL } from '../../config';
import { useDraftListing } from '../../contexts/DraftListingContext';
import {
  useFieldUpdater,
  useListingActions,
  useListingField,
  useListingStatus,
  useMultipleStatuses,
} from '@brightmls/nextgen.lae.flc';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';
import { FeatureFlags } from 'src/flags';
import { useDefaults } from '../../contexts/DefaultsContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantPath } from '../../hooks/useTenantPath';
import { useMetadata } from '../../contexts/MetadataContext';
import { useManageListingsNavigation } from '../../hooks/useManageListingsNavigation';

const CreateDraft: React.FC = () => {
  const { showPanel } = useSidePanel();
  const { captureEvent } = useAnalytics();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { assistant } = useDraftListing();
  const { saveListing } = useListingActions();
  const { defaults } = useDefaults();
  const { hasFeature } = useFeatureFlags();
  const updateFields = useFieldUpdater();
  const { getPath } = useTenantPath();
  const { field } = useMetadata();
  const { back } = useManageListingsNavigation();

  const hasSetFieldDefaults = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);
  const hasSubPropertyTypeFeature = hasFeature(
    FeatureFlags.CREATE_SUB_PROPERTY_TYPE_FILTER,
  );

  const metadataHasSubPropertyType = Boolean(
    field(PropertyField.PROPERTY_SUB_TYPE),
  );

  const unparsedAddress = useListingField(
    ListingResource.Property,
    PropertyField.UNPARSED_ADDRESS,
    '',
  );

  const propertyType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );

  const propertySubType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_SUB_TYPE,
    '',
  );

  const { values: statuses } = useMultipleStatuses(
    ['invalidPhone', 'ready', 'no_tax_record_mode', 'touched'],
    {
      invalidPhone: false,
      ready: false,
      no_tax_record_mode: false,
      touched: false,
    },
  );

  // Track draft listing creation across the application
  const isCreatingDraftListing = useListingStatus<boolean>('isCreating', false);

  const getButtonDisabledStatus = useCallback(() => {
    // Check if required fields are empty
    const hasPropertySubTypeValue =
      hasSubPropertyTypeFeature && metadataHasSubPropertyType
        ? propertySubType.value
        : true; // if the tenant doesn't have the feature, we just let this check pass

    const hasEmptyData =
      !unparsedAddress.value || !propertyType.value || !hasPropertySubTypeValue;

    // If: address is missing, is currently submitting the form
    //     or is pending listing assistant information, disable submission
    if (hasEmptyData || isCreatingDraftListing.value || !assistant) return true;

    // Use our beautiful status values directly!
    if (statuses.invalidPhone) return true;

    if (statuses.ready || statuses.no_tax_record_mode) {
      // assistant is ready and records are empty, button should not be disabled
      if (assistant.records.length === 0) return false;

      return !!(statuses.ready && statuses.touched);
    }

    if (statuses.touched) return true;

    return true;
  }, [
    unparsedAddress.value,
    propertyType.value,
    hasSubPropertyTypeFeature,
    metadataHasSubPropertyType,
    propertySubType.value,
    assistant,
    statuses,
    isCreatingDraftListing.value,
  ]);

  const isButtonDisabled = getButtonDisabledStatus();

  const handleCreateDraft = async () => {
    // Tell the rest of the state store that we're creating a draft listing
    isCreatingDraftListing.updateValue(true);

    setError(null);
    try {
      if (hasSetFieldDefaults) {
        updateFields(await defaults());
      }

      const R = await saveListing();

      if (R.error) {
        throw new Error(R.error);
      }

      const listingKey = R.ListingKey;

      captureEvent(ApplicationEvent.ListingCreate, {
        listingKey,
        listingId: R.ListingId,
        propertyType: propertyType.value,
        address: unparsedAddress.value,
      });

      navigate(getPath(`/edit/${listingKey}`));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : JSON.stringify(err);
      setError(errorMessage);
      showPanel(
        'Error',
        <div>
          <Typography variant="h6">
            There was an issue creating the draft listing
          </Typography>
          <pre>{errorMessage}</pre>
        </div>,
      );
    } finally {
      // Always unset the status once we're done (failure or success)
      isCreatingDraftListing.updateValue(false);
    }
  };

  const handleBackToManage = () => {
    back();
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 'unset' },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          justifyContent: {
            xs: 'flex-start', // Left-aligned on mobile
            md: 'flex-end', // Right-aligned on desktop
          },
        }}
      >
        <Button
          onClick={handleBackToManage}
          variant="outlined"
          sx={{
            textTransform: 'none',
          }}
          data-testid="back-to-manage-button"
        >
          Back To Manage Listings
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isButtonDisabled}
          onClick={handleCreateDraft}
          sx={{ textTransform: 'none' }}
          data-testid="create-draft-button"
        >
          {isCreatingDraftListing.value
            ? 'Creating...'
            : 'Create Draft Listing'}
        </Button>
      </Stack>

      {error && (
        <div data-testid="error-message">
          <Typography variant="h6">
            There was an issue creating the draft listing
          </Typography>
          <pre>{error}</pre>
        </div>
      )}
    </Box>
  );
};

export default CreateDraft;
